import { Suspense } from 'react';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';

import { IconLoader2 } from '@tabler/icons-react';

import type { PublicClientApplication } from '@azure/msal-browser';

import AppLayout from './layout/AppLayout';
import InteractiveAuthGate from './components/InteractiveAuthGate';
import { CustomNavigationClient } from './lib/NavigationClient';
import { hasAuthTokens } from './lib/authToken';
import Applications from './modules/application';
import Settings from './modules/settings';
import Evaluation from './modules/evaluation';
import ReleaseNotes from './modules/release-notes';
import { reactPlugin } from './config/telemetry';

interface AppProps {
  msalInstance: PublicClientApplication;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

const SuspenseFallback = () => (
  <div className="flex h-full w-full items-center justify-center">
    <IconLoader2 className="animate-spin text-[#005b92]" size={32} />
  </div>
);

const OnboardRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/applications/onboard${search}`} replace />;
};

function App({ msalInstance }: AppProps) {
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  msalInstance.setNavigationClient(navigationClient);
  const authTokenMode = hasAuthTokens();

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <Routes>
            <Route
              element={
                authTokenMode ? (
                  <AppLayout />
                ) : (
                  <InteractiveAuthGate>
                    <AppLayout />
                  </InteractiveAuthGate>
                )
              }
            >
              <Route path="" element={<Navigate to="applications" replace />} />
              <Route
                path="applications/*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Applications />
                  </Suspense>
                }
              />
              <Route
                path="newsletter/*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <ReleaseNotes />
                  </Suspense>
                }
              />
              <Route path="onboard" element={<OnboardRedirect />} />
              <Route
                path="settings/*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Settings />
                  </Suspense>
                }
              />
              <Route
                path="evaluations/*"
                element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <Evaluation />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
        </QueryClientProvider>
      </MsalProvider>
    </AppInsightsContext.Provider>
  );
}

export default App;
